// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState } from 'react';
import { Button, ToastNotification } from '@carbon/react';
import type { ProcedureDetail, PolicyDetail } from '../../shared/types/procedures';
import styles from './ProcedureJsonPane.module.css';

interface ProcedureJsonPaneProps {
  procedure: ProcedureDetail | PolicyDetail;
}

type CopyStatus = 'idle' | 'success' | 'error';

/**
 * "Advanced: raw JSON" pane — read-only pretty-printed dump of whatever
 * detail value `ProcedureForm`/`ProcedurePreview` (or `PolicyForm`/
 * `PolicyPreview`) render, so all three always agree. Shared by both
 * `ProcedureDetail` and `PolicyDetail` — the union keeps at least some type
 * check on this pane instead of widening to `object`.
 * No JSON editor dependency: this screen is view-only, a plain `<pre>` is
 * enough.
 */
export default function ProcedureJsonPane({ procedure }: ProcedureJsonPaneProps) {
  const json = JSON.stringify(procedure, null, 2);
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  function handleCopy() {
    navigator.clipboard
      .writeText(json)
      .then(() => setCopyStatus('success'))
      .catch(() => setCopyStatus('error'));
  }

  return (
    <div className={styles.pane}>
      <div className={styles.box}>
        <div className={styles.header}>
          <span className={styles.label}>JSON</span>
          <Button kind="tertiary" size="sm" onClick={handleCopy}>
            Copy
          </Button>
        </div>
        <pre className={styles.json}>{json}</pre>
      </div>
      {copyStatus !== 'idle' && (
        <ToastNotification
          className={styles.toast}
          kind={copyStatus === 'success' ? 'success' : 'error'}
          title={copyStatus === 'success' ? 'Copied to clipboard' : 'Copy failed'}
          subtitle={copyStatus === 'error' ? 'Clipboard access was denied.' : undefined}
          timeout={3000}
          onClose={() => setCopyStatus('idle')}
          lowContrast
        />
      )}
    </div>
  );
}
